import React, { useEffect, useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    makeStyles,
    Theme,
    Select,
    MenuItem,
    FormControl,
    TextField,
    Grid,
} from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { tr } from 'utils/translations/translate'
import { CompanyInfo, Contract, ResidentialInfo } from 'models/contract'
import Spinner from 'common/spinner'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/app/app-store'
import { Product, ProductStatus } from 'models/product'
import InvoiceClient from 'clients/invoice-client'
import { toShortDate } from 'utils/time/timeUtil'
import { InvoiceCreatePeriod } from 'models/invoice-create-period'
import { CreateInvoiceLine, InvoiceParty } from 'models/create-invoice'
import classNames from 'classnames'
import { getDecimalString } from 'utils/invoice/invoice-utils'
import {
    TEXT_E_MAIL,
    TEXT_INVOICE_ADDRESS,
    TEXT_INVOICE_RECEIVER,
    TEXT_INVOICE_REFERENCE,
    TEXT_POST_CODE,
    TEXT_SUB_COMPANY,
    TEXT_UTILITY_UNIT,
} from 'utils/translations/keys'
import { formatVatPercentage } from './contract-helper'
import { useCommonStyles } from './contract-invoices-styles'

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        marginTop: theme.spacing(1),
    },
    tableHead: {
        fontWeight: 600,
        borderBottom: 'none',
        paddingLeft: 0,
    },
    tableColumn: {
        borderBottom: 'none',
        paddingLeft: 0,
    },
    dialogTitle: {
        paddingBottom: '0',
    },
    titleCase: {
        textTransform: 'capitalize',
    },
    invoiceReceiverSelect: {
        display: 'inline-block',
        width: '300px',
        verticalAlign: 'middle',
    },
    infoField: {
        lineHeight: '32px',
        verticalAlign: 'middle',
        display: 'inline-block',
        width: '128px',
    },
    addNewProduct: {
        width: '400px',
        borderRadius: 0,
    },
    alignRight: {
        textAlign: 'right',
    },
    recieverSelectionContent: {
        alignContent: 'center',
    },
    invoiceDialogStyle: {
        width: '80vw',
        height: '60vh',
        maxWidth: 1600,
    },
}))

type CreateNewInvoiceProps = {
    contract: Contract
    open: boolean
    handleOk: () => void
    handleClose: () => void
}

export const CreateNewInvoice: React.FC<CreateNewInvoiceProps> = (props) => {
    const { open, handleClose, handleOk, contract } = props

    const commonStyles = useCommonStyles()
    const styles = useStyles()
    const tenantConfig = useSelector((state: AppState) => state.config.tenantConfig)
    const [periodInfo, setPeriodInfo] = useState<InvoiceCreatePeriod>()
    const [lines, setLines] = useState<CreateInvoiceLine[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [createInvoiceDisabled, setCreateInvoiceDisabled] = useState(false)
    const products = useSelector((state: AppState) => state.products.items)
    const residentialInfoProducts = contract.residentialInfo.flatMap((x) => x.products)
    const [selectedInvoiceReceiver, setSelectedInvoiceReceiver] = useState<InvoiceParty | undefined>(undefined)
    const [selectedInvoiceReceiverHousingUnit, setSelectedInvoiceReceiverHousingUnit] = useState<
        ResidentialInfo | undefined
    >(undefined)
    const [selectedInvoiceReceiverCompany, setSelectedInvoiceReceiverCompany] = useState<CompanyInfo | undefined>(
        undefined,
    )
    const [nsoDescription, setNsoDescription] = useState('')
    const [selectedProduct, setSelectedProduct] = useState<string>()

    const [selectedLineIndex, setSelectedLineIndex] = useState<number>(0)

    const activeProducts =
        tenantConfig?.isContractBasedInvoicing &&
        contract.residentialInfo &&
        contract.residentialInfo.length &&
        residentialInfoProducts.length
            ? products.filter(
                  (x) =>
                      (x.status || {}).value === ProductStatus.Active &&
                      residentialInfoProducts.findIndex((cp) => x.productId === cp.productId) !== -1,
              )
            : products.filter((x) => (x.status || {}).value === ProductStatus.Active)

    useEffect(() => {
        if (
            tenantConfig?.isNsoTenant &&
            lines &&
            selectedInvoiceReceiverCompany?.organizationNumber &&
            periodInfo &&
            selectedProduct
        ) {
            getInvoiceLineDescription(
                selectedInvoiceReceiverCompany?.organizationNumber,
                selectedProduct,
                periodInfo.periodYear,
            )
        }
    }, [selectedLineIndex, selectedProduct])

    useEffect(() => {
        if (open) {
            setPeriod()
            setLines([])
            setCreateInvoiceDisabled(false)
        }
    }, [open])

    useEffect(() => {
        if (tenantConfig?.isNsoTenant && nsoDescription) {
            const updatedLines = lines.map((x, i) => {
                if (i === selectedLineIndex) {
                    return {
                        ...x,
                        description: nsoDescription,
                    }
                } else {
                    return x
                }
            })
            setLines(updatedLines)
            setNsoDescription('')
        }
    }, [nsoDescription])

    const getInvoiceLineDescription = async (organizationNumber: string, productId: string, periodYear: number) => {
        const description = await InvoiceClient.getNewInvoiceDescription(
            contract.id,
            organizationNumber,
            productId,
            periodYear,
        ).toPromise()
        setNsoDescription(description)
    }

    const setPeriod = async () => {
        setIsLoading(true)
        setPeriodInfo(await InvoiceClient.getInvoiceCreationPeriod().toPromise())
        setIsLoading(false)
    }

    const addNewProduct = () => {
        if (!periodInfo) {
            return
        }

        const lineNo = Math.max(...lines.map((p) => p.lineNo), 1) + 1

        setLines([
            ...lines,
            {
                invoiceDate: periodInfo.invoiceDate,
                period: periodInfo.period,
                periodYear: periodInfo.periodYear,
                numerator: 1,
                denominator: 1,
                description: '',
                lineNo: lineNo,
                invoiceParty: undefined,
            },
        ])
    }

    const calculateLinePrice = (line: CreateInvoiceLine, product: Product | undefined) => {
        if (!product || !periodInfo) {
            return null
        }
        const lineTotal = product.price * (line.numerator / line.denominator)
        return product.vatPercentage > 0 ? lineTotal + product.vatPercentage / 100 : lineTotal
    }

    const chooseProduct = (line: CreateInvoiceLine, productId: string, index: number) => {
        const updatedLines = [...lines]
        const updatedLine = { ...line, productId: productId }
        updatedLines.splice(index, 1, updatedLine)
        setLines(updatedLines)
        setSelectedProduct(productId)
        setSelectedLineIndex(index)
    }

    const changeLineDenominator = (line: CreateInvoiceLine, denominator: number, index: number) => {
        const updatedLines = [...lines]
        const updatedLine = { ...line, denominator: denominator }
        updatedLines.splice(index, 1, updatedLine)
        setLines(updatedLines)
    }

    const changeLineNumerator = (line: CreateInvoiceLine, numerator: number, index: number) => {
        const updatedLines = [...lines]
        const updatedLine = { ...line, numerator: numerator }
        updatedLines.splice(index, 1, updatedLine)
        setLines(updatedLines)
    }

    const removeLine = (line: CreateInvoiceLine) => {
        const _lines = [...lines]
        _lines.splice(
            _lines.findIndex((x) => x === line),
            1,
        )
        setLines(_lines)
    }

    const createInvoicesOk = async () => {
        setCreateInvoiceDisabled(true)

        if (!lines || !lines.length || lines.findIndex((x) => !x.productId) !== -1) {
            return
        }

        setIsLoading(true)

        const linesWithNewInvoiceReceiver = lines.map((x) => {
            return { ...x, invoiceParty: selectedInvoiceReceiver }
        })

        await InvoiceClient.createInvoiceLines({
            contractId: contract.id,
            invoiceLines: linesWithNewInvoiceReceiver,
            isCrediting: false,
            companyGroupKey: selectedInvoiceReceiverCompany?.groupKey ?? '',
            housingUnit: selectedInvoiceReceiverHousingUnit,
        }).toPromise()

        setIsLoading(false)
        handleOk()
    }

    const onChangeDescription = (line: CreateInvoiceLine, description: string, index: number) => {
        const updatedLines = [...lines]
        const updatedLine = { ...line, description: description }
        updatedLines.splice(index, 1, updatedLine)
        setLines(updatedLines)
    }

    const onChangeHousingUnit = (e: any) => {
        setSelectedInvoiceReceiverHousingUnit(contract.residentialInfo.find((x) => x.uniqueKey === e.target.value))
        const foundParty = contract.parties.find(
            (x) => x.address.streetAddress.toUpperCase() === e.target.value.toUpperCase(),
        )
        setSelectedInvoiceReceiver(foundParty ?? contract.parties[0])
    }

    const onChangeCompany = (e: any) => {
        setSelectedInvoiceReceiverCompany(contract.companies.find((x) => x.groupKey === e.target.value))
    }

    const renderGridItem = (labelText: string, value: any) => {
        return (
            <>
                <Grid item xs={6} sm={4} md={3} lg={2} xl={1}>
                    <strong className={styles.infoField}>{labelText}</strong>
                </Grid>
                <Grid item xs={6} sm={8} md={9} lg={10} xl={11} className={styles.recieverSelectionContent}>
                    {value}
                </Grid>
            </>
        )
    }

    return (
        <Dialog open={open} maxWidth="xl">
            <DialogTitle className={styles.dialogTitle}>{tr('TEXT_CREATE_NEW_INVOICE')}</DialogTitle>
            <DialogContent className={commonStyles.invoiceDialogStyle}>
                {isLoading && <Spinner />}
                {!isLoading && (
                    <>
                        {!tenantConfig?.isNsoTenant && (
                            <Grid container>
                                <Grid item xs={6} sm={4} md={3} lg={2} xl={1}>
                                    <strong className={styles.infoField}>{tr(TEXT_UTILITY_UNIT)}</strong>
                                </Grid>
                                <Grid item xs={6} sm={8} md={9} lg={10} xl={11}>
                                    <FormControl
                                        variant="standard"
                                        style={{ maxWidth: '300px', verticalAlign: 'middle' }}
                                    >
                                        <Select
                                            value={selectedInvoiceReceiverHousingUnit?.uniqueKey ?? ''}
                                            onChange={(e) => onChangeHousingUnit(e)}
                                        >
                                            {contract.residentialInfo.map((x, index) => (
                                                <MenuItem value={x.uniqueKey} key={x.uniqueKey}>
                                                    <span>{`${x.address} ${x.residentialNumber}, ${x.buildingNumber}`}</span>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {renderGridItem(
                                    tr(TEXT_INVOICE_RECEIVER),
                                    selectedInvoiceReceiver
                                        ? selectedInvoiceReceiver?.companyName
                                            ? selectedInvoiceReceiver.companyName
                                            : `${selectedInvoiceReceiver.firstName} ${selectedInvoiceReceiver.lastName}`
                                        : '',
                                )}
                                {renderGridItem(
                                    tr(TEXT_INVOICE_ADDRESS),
                                    selectedInvoiceReceiver?.address.streetAddress,
                                )}
                                {renderGridItem(
                                    tr(TEXT_POST_CODE),
                                    <>
                                        {selectedInvoiceReceiver?.address.postalCode}
                                        <div>{selectedInvoiceReceiver?.address.city}</div>
                                    </>,
                                )}
                            </Grid>
                        )}

                        {tenantConfig?.isNsoTenant && (
                            <Grid container>
                                <Grid item xs={6} sm={4} md={3} lg={2} xl={1}>
                                    <strong className={styles.infoField}>{tr(TEXT_SUB_COMPANY)}</strong>
                                </Grid>
                                <Grid item xs={6} sm={8} md={9} lg={10} xl={11}>
                                    <FormControl
                                        variant="standard"
                                        style={{ maxWidth: '300px', verticalAlign: 'middle' }}
                                    >
                                        <Select
                                            value={selectedInvoiceReceiverCompany?.groupKey ?? ''}
                                            onChange={(e) => onChangeCompany(e)}
                                        >
                                            {contract.companies.map((x, index) => (
                                                <MenuItem value={x.groupKey} key={x.groupKey}>
                                                    <span>{x.companyName}</span>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {renderGridItem(tr(TEXT_INVOICE_RECEIVER), selectedInvoiceReceiverCompany?.companyName)}
                                {renderGridItem(tr(TEXT_INVOICE_ADDRESS), selectedInvoiceReceiverCompany?.address)}
                                {renderGridItem(
                                    tr(TEXT_POST_CODE),
                                    <>
                                        {selectedInvoiceReceiverCompany?.postalCode}
                                        <div>{selectedInvoiceReceiverCompany?.city}</div>
                                    </>,
                                )}
                                {renderGridItem(tr(TEXT_E_MAIL), selectedInvoiceReceiverCompany?.email)}
                                {renderGridItem(
                                    tr(TEXT_INVOICE_REFERENCE),
                                    selectedInvoiceReceiverCompany?.invoiceReference,
                                )}
                            </Grid>
                        )}

                        <Table className={styles.table} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className={styles.tableHead}>{tr('TEXT_TERMIN')}</TableCell>
                                    <TableCell className={styles.tableHead}>{tr('TEXT_INVOICE_DATE')}</TableCell>
                                    <TableCell className={styles.tableHead}>{tr('TEXT_PRODUCT')}</TableCell>
                                    <TableCell className={styles.tableHead}>{tr('TEXT_DESCRIPTION')}</TableCell>
                                    <TableCell className={styles.tableHead}>{tr('TEXT_FRACTION')}</TableCell>
                                    <TableCell className={styles.tableHead}>{tr('TEXT_PRICE')}</TableCell>
                                    <TableCell className={styles.tableHead}>{tr('TEXT_MVA')}</TableCell>
                                    <TableCell className={styles.tableHead}>{tr('TEXT_SUM')}</TableCell>
                                    <TableCell className={styles.tableHead}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lines.map((line, index) => {
                                    const product = activeProducts.find((x) => x.id === line.productId)
                                    return (
                                        <TableRow key={line.lineNo}>
                                            <TableCell className={styles.tableColumn}>
                                                {line.period} {line.periodYear}
                                            </TableCell>
                                            <TableCell className={styles.tableColumn}>
                                                {toShortDate(line.invoiceDate) || '-'}
                                            </TableCell>
                                            <TableCell className={styles.tableColumn}>
                                                <FormControl variant="standard" style={{ minWidth: '300px' }}>
                                                    <Select
                                                        value={line.productId || ''}
                                                        onChange={(item) =>
                                                            chooseProduct(line, item.target.value as string, index)
                                                        }
                                                    >
                                                        {activeProducts.map((product) => (
                                                            <MenuItem value={product.id} key={product.id}>
                                                                <span>{product.name}</span>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={styles.tableColumn}>
                                                <TextField
                                                    value={line.description}
                                                    onChange={(text) =>
                                                        onChangeDescription(line, text.target.value, index)
                                                    }
                                                ></TextField>
                                            </TableCell>
                                            <TableCell className={classNames(styles.tableColumn, styles.alignRight)}>
                                                <FormControl
                                                    variant="standard"
                                                    style={{ maxWidth: '40px', verticalAlign: 'middle' }}
                                                >
                                                    <Select
                                                        value={line.numerator}
                                                        onChange={(item) =>
                                                            changeLineNumerator(
                                                                line,
                                                                item.target.value as number,
                                                                index,
                                                            )
                                                        }
                                                    >
                                                        {[...Array(20)].map((_, index) => (
                                                            <MenuItem value={index + 1} key={index}>
                                                                <span>{index + 1}</span>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <span> / </span>
                                                <FormControl
                                                    variant="standard"
                                                    style={{ maxWidth: '40px', verticalAlign: 'middle' }}
                                                >
                                                    <Select
                                                        value={line.denominator}
                                                        onChange={(item) =>
                                                            changeLineDenominator(
                                                                line,
                                                                item.target.value as number,
                                                                index,
                                                            )
                                                        }
                                                    >
                                                        {[...Array(20)].map((_, index) => (
                                                            <MenuItem value={index + 1} key={index}>
                                                                <span>{index + 1}</span>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell className={classNames(styles.tableColumn, styles.alignRight)}>
                                                {getDecimalString(product?.price ?? 0)}
                                            </TableCell>
                                            <TableCell className={classNames(styles.tableColumn, styles.alignRight)}>
                                                {formatVatPercentage(product?.vatPercentage ?? 0)}
                                            </TableCell>
                                            <TableCell className={classNames(styles.tableColumn, styles.alignRight)}>
                                                {getDecimalString(calculateLinePrice(line, product) ?? 0)}
                                            </TableCell>
                                            <TableCell className={styles.tableColumn}>
                                                <DeleteOutlineIcon
                                                    style={{ color: '#aaa', cursor: 'pointer' }}
                                                    onClick={() => removeLine(line)}
                                                ></DeleteOutlineIcon>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </>
                )}
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <Button variant="outlined" className={styles.addNewProduct} onClick={addNewProduct}>
                        {tr('TEXT_ADD_PRODUCT')}
                    </Button>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    {tr('TEXT_CANCEL')}
                </Button>
                <Button
                    onClick={createInvoicesOk}
                    disabled={createInvoiceDisabled}
                    variant="contained"
                    color="primary"
                    autoFocus
                >
                    {tr('TEXT_CREATE_NEW_INVOICE')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
